import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Btn, Field, TextInput } from "../components/Primitives";
import { toast } from "sonner";
import { ShieldCheck, Sparkles, Lock, FileCheck2, Radio, Users, ArrowRight, Check } from "lucide-react";

const FEATURES = [
  { icon: FileCheck2, label: "22 modules · deep coverage" },
  { icon: ShieldCheck, label: "RBAC across 9 roles" },
  { icon: Sparkles, label: "AI drafting · Claude Sonnet 4.5" },
  { icon: Radio, label: "Breach + CERT-In workflows" },
];

export default function Login() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("admin@sanghvi.example");
  const [password, setPassword] = useState("Password@123");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") await login(email, password);
      else await register(email, password, name);
      navigate("/app/dashboard");
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };

  const demos = [
    { role: "Org Admin", email: "admin@sanghvi.example" },
    { role: "DPO", email: "dpo@sanghvi.example" },
    { role: "Legal", email: "legal@sanghvi.example" },
    { role: "Security", email: "security@sanghvi.example" },
    { role: "Business Owner", email: "owner@sanghvi.example" },
    { role: "Vendor Manager", email: "vendor@sanghvi.example" },
    { role: "Auditor", email: "auditor@sanghvi.example" },
  ];

  return (
    <div className="min-h-screen bg-app flex flex-col lg:flex-row">
      {/* Hero */}
      <div className="lg:w-[55%] relative overflow-hidden" style={{ background: "linear-gradient(135deg, #0B1220 0%, #1E1B4B 45%, #4C1D95 100%)" }}>
        <div className="absolute inset-0 grid-pattern-dark opacity-50" />
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full blur-3xl opacity-30" style={{ background: "#4F46E5" }} />
        <div className="absolute bottom-0 left-0 w-96 h-96 rounded-full blur-3xl opacity-20" style={{ background: "#10B981" }} />

        <div className="relative z-10 h-full flex flex-col p-8 lg:p-14 text-white">
          <div className="flex items-center gap-2.5 mb-16">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "rgba(255,255,255,0.1)", backdropFilter: "blur(10px)", border: "1px solid rgba(255,255,255,0.15)" }}>
              <ShieldCheck className="w-5 h-5 text-white" strokeWidth={2} />
            </div>
            <div>
              <div className="font-semibold text-base leading-tight">DPDP Control Plane</div>
              <div className="text-[10px] mono uppercase tracking-widest opacity-60">Compliance Operating System</div>
            </div>
          </div>

          <div className="flex-1 flex flex-col justify-center max-w-lg">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium mb-5 self-start" style={{ background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.15)" }}>
              <span className="w-1.5 h-1.5 rounded-full pulse-ring" style={{ background: "#10B981" }} />
              <span>DPDP Act 2023 · Rules 2025 · CERT-In</span>
            </div>
            <h1 className="text-4xl lg:text-5xl font-semibold leading-[1.05] tracking-[-0.02em]">
              Ship DPDP compliance{" "}
              <span className="serif italic" style={{ color: "#A5B4FC" }}>without the spreadsheets.</span>
            </h1>
            <p className="mt-5 text-[15px] leading-relaxed" style={{ color: "rgba(255,255,255,0.75)" }}>
              An operational control plane for India's Digital Personal Data Protection Act — data inventory,
              notices, consents, rights requests, breach command, DPIA, vendor governance, and regulator-ready
              evidence in one workspace.
            </p>

            <div className="mt-8 grid grid-cols-2 gap-3">
              {FEATURES.map((f) => (
                <div key={f.label} className="flex items-center gap-2.5 text-sm" style={{ color: "rgba(255,255,255,0.85)" }}>
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)" }}>
                    <f.icon className="w-4 h-4" strokeWidth={1.75} style={{ color: "#A5B4FC" }} />
                  </div>
                  <span>{f.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Trust footer */}
          <div className="mt-16 flex items-center gap-6 text-[11px] mono uppercase tracking-wider" style={{ color: "rgba(255,255,255,0.5)" }}>
            <div className="flex items-center gap-1.5"><Lock className="w-3 h-3" /> Multi-tenant</div>
            <div className="flex items-center gap-1.5"><ShieldCheck className="w-3 h-3" /> RBAC</div>
            <div className="flex items-center gap-1.5"><FileCheck2 className="w-3 h-3" /> Audit trail</div>
          </div>
        </div>
      </div>

      {/* Sign in panel */}
      <div className="lg:w-[45%] flex items-center justify-center p-6 lg:p-12 bg-app">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <div className="label-cap mb-2">{mode === "login" ? "Sign in" : "Register"}</div>
            <h2 className="text-3xl font-semibold ink tracking-tight">
              {mode === "login" ? "Welcome back" : "Create your organization"}
            </h2>
            <p className="text-sm muted mt-1.5">
              {mode === "login" ? "Continue to your compliance workspace." : "Set up a new tenant workspace."}
            </p>
          </div>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <Field label="Full name" required>
                <TextInput data-testid="register-name" value={name} onChange={(e) => setName(e.target.value)} required />
              </Field>
            )}
            <Field label="Work email" required>
              <TextInput data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </Field>
            <Field label="Password" required>
              <TextInput data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
            </Field>
            <Btn data-testid={mode === "login" ? "login-submit-btn" : "register-submit-btn"} type="submit" size="lg" className="w-full justify-center" disabled={loading}>
              {loading ? "Please wait..." : mode === "login" ? "Sign in" : "Create account"} <ArrowRight className="w-4 h-4" />
            </Btn>
            <button
              type="button"
              data-testid="toggle-auth-mode"
              onClick={() => setMode(mode === "login" ? "register" : "login")}
              className="w-full text-xs muted hover:ink pt-1 transition-colors"
            >
              {mode === "login" ? "New organization? Register →" : "Already have an account? Sign in →"}
            </button>
          </form>

          <div className="mt-8">
            <div className="flex items-center gap-3 mb-3">
              <div className="flex-1 h-px" style={{ background: "var(--border)" }} />
              <div className="label-cap">Demo accounts · password Password@123</div>
              <div className="flex-1 h-px" style={{ background: "var(--border)" }} />
            </div>
            <div className="grid grid-cols-1 gap-1.5">
              {demos.map((a) => (
                <button
                  key={a.email}
                  type="button"
                  data-testid={`demo-${a.role.replace(/\s+/g, "-").toLowerCase()}`}
                  onClick={() => { setMode("login"); setEmail(a.email); setPassword("Password@123"); }}
                  className="text-left text-xs surface flex justify-between items-center px-3 py-2 rounded-lg transition-all hover:card-shadow"
                  style={{ border: "1px solid var(--border)" }}
                >
                  <div className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full" style={{ background: "var(--primary)" }} />
                    <span className="font-medium ink">{a.role}</span>
                  </div>
                  <span className="mono muted-2 text-[10.5px]">{a.email}</span>
                </button>
              ))}
            </div>
            <a href="/portal" data-testid="link-data-principal-portal" className="mt-5 flex items-center justify-center gap-1.5 text-xs font-medium primary hover:opacity-80">
              <Users className="w-3.5 h-3.5" /> Are you a Data Principal? Public portal →
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
